import React, { useState } from 'react';
import { 
  Plus, ArrowRight, Settings, Users, 
  Clock, AlertTriangle, DollarSign 
} from 'lucide-react';

const WorkflowSettings = () => {
  const [workflows, setWorkflows] = useState([
    {
      id: 'default',
      name: 'Default Workflow',
      description: 'Standard approval process for general maintenance tickets',
      steps: [
        { id: 1, type: 'submit', role: 'USER', action: 'Submit ticket' },
        { id: 2, type: 'approve', role: 'MANAGER', action: 'Department manager approval' },
        { id: 3, type: 'assign', role: 'MAINTENANCE_MANAGER', action: 'Assign to maintenance staff' },
        { id: 4, type: 'complete', role: 'MAINTENANCE_STAFF', action: 'Complete work' },
        { id: 5, type: 'verify', role: 'USER', action: 'Verify completion' }
      ],
      conditions: {
        costThreshold: 1000,
        priorityLevels: ['low', 'medium', 'high'],
        departments: ['Operations', 'Facilities']
      }
    }
  ]);

  const [selectedWorkflow, setSelectedWorkflow] = useState(workflows[0]);

  return (
    <div className="divide-y divide-gray-200">
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <Settings className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Workflow Configuration
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Manage approval flows and automation rules
              </p>
            </div>
          </div>
          <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 
                         shadow-sm hover:shadow transition-all flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Workflow
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-5 gap-6">
          {/* Workflow Selection */}
          <div className="col-span-2 space-y-3">
            {workflows.map(workflow => (
              <button
                key={workflow.id}
                onClick={() => setSelectedWorkflow(workflow)}
                className={`w-full p-4 text-left rounded-xl border transition-all ${
                  selectedWorkflow.id === workflow.id
                    ? 'border-blue-200 bg-gradient-to-r from-blue-50/50 to-transparent shadow-sm'
                    : 'border-gray-200 hover:border-blue-200 hover:bg-gray-50'
                }`}
              >
                <h3 className="font-semibold text-gray-900">{workflow.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{workflow.description}</p>
              </button>
            ))}
          </div>

          {/* Workflow Details */}
          <div className="col-span-3 space-y-6">
            {/* Steps Configuration */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <Clock className="w-5 h-5 text-blue-500" />
                Approval Steps
              </h3>
              <div className="space-y-3">
                {selectedWorkflow.steps.map((step, index) => (
                  <div
                    key={step.id}
                    className="flex items-center gap-4 p-4 rounded-xl bg-white border border-gray-200 hover:bg-gray-50/50 transition-all"
                  >
                    <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center font-bold shadow-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{step.action}</p>
                      <p className="text-sm text-blue-700">Role: {step.role}</p>
                    </div>
                    <button className="p-2 hover:bg-blue-50 rounded-lg transition-colors">
                      <Settings className="w-5 h-5 text-gray-400 hover:text-blue-600" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Conditions with Enhanced Design */}
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Workflow Conditions
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-100 hover:bg-amber-50/80 transition-all">
                  <div className="flex items-center gap-2 text-gray-900 font-semibold">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <DollarSign className="w-5 h-5 text-amber-600" />
                    </div>
                    <span>Cost Threshold</span>
                  </div>
                  <p className="mt-2 text-sm text-amber-700 font-medium">
                    Applies to tickets over ${selectedWorkflow.conditions.costThreshold}
                  </p>
                </div>
                
                <div className="p-4 rounded-xl bg-violet-50 border border-violet-100 hover:bg-violet-50/80 transition-all">
                  <div className="flex items-center gap-2 text-gray-900 font-semibold">
                    <div className="p-2 rounded-lg bg-violet-100">
                      <AlertTriangle className="w-5 h-5 text-violet-600" />
                    </div>
                    <span>Priority Levels</span>
                  </div>
                  <p className="mt-2 text-sm text-violet-700 font-medium">
                    {selectedWorkflow.conditions.priorityLevels.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowSettings; 